'use client';

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Check,
  Video,
  Building2,
  Home,
  Clock,
  ShieldCheck,
  QrCode,
  CreditCard,
  Copy,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  User,
  Phone,
  Mail,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { schedulingApi, filesApi, patientsApi } from '@/api';
import { paymentsApi } from '@/api/payments-api';
import { authApi, httpClient } from '@/api/http-client';
import { GoogleLoginButton } from '@/components/auth/GoogleLoginButton';
import { Captcha } from '@/components/common/Captcha';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useToast } from '@/providers/ToastProvider';

export interface DoctoraliaBookingDoctor {
  id: string;
  name: string;
  specialty: string;
  crm?: string;
  photoUrl?: string;
  price?: number;
  inPersonPrice?: number;
  homeCarePrice?: number;
  hasTelemedicine?: boolean;
  hasInPerson?: boolean;
  hasHomeCare?: boolean;
  avatarColor?: { bg: string; text: string };
}

export interface DoctoraliaBookingSlot {
  dayLabel: string;
  dateStr: string;
  time: string;
  fullDateIso?: string;
}

export interface DoctoraliaBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctor: DoctoraliaBookingDoctor;
  initialSlot?: DoctoraliaBookingSlot | null;
  initialModality?: 'video' | 'office' | 'home';
}

export function DoctoraliaBookingModal({
  isOpen,
  onClose,
  doctor,
  initialSlot,
  initialModality = 'video',
}: DoctoraliaBookingModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Step 1: Modalidade & Horário | Step 2: Identificação | Step 3: Pagamento | Step 4: Confirmação
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [realAppointmentId, setRealAppointmentId] = useState<string | null>(null);

  // Form State
  const [modality, setModality] = useState<'video' | 'office' | 'home'>(initialModality);
  const [selectedSlot, setSelectedSlot] = useState<DoctoraliaBookingSlot>(
    initialSlot || {
      dayLabel: 'Hoje',
      dateStr: new Date().toLocaleDateString('pt-BR'),
      time: '14:00',
    }
  );

  // Patient Info
  const [patientName, setPatientName] = useState('');
  const [patientCpf, setPatientCpf] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientComplaint, setPatientComplaint] = useState('');

  // Auth in-modal state (para pacientes não identificados)
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const { login: executeLogin } = useAuth();

  // PagBank Payment State
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [pixData, setPixData] = useState<{
    paymentId: string;
    qrCode?: string;
    qrCodeUrl?: string;
    copyPaste?: string;
    checkoutUrl?: string;
  } | null>(null);
  const paymentPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Carrega dados do perfil quando o usuário já estiver logado
  useEffect(() => {
    if (user?.id) {
      setPatientName(user.name || '');
      setPatientEmail(user.email || '');
      patientsApi.getProfile()
        .then((profile) => {
          if (profile) {
            if (profile.name) setPatientName(profile.name);
            if (profile.email) setPatientEmail(profile.email);
            if (profile.cpf) setPatientCpf(formatCpf(profile.cpf));
            if (profile.phone) setPatientPhone(formatPhone(profile.phone));
          }
        })
        .catch(() => {});
    }
  }, [user]);

  // Payment Info
  const [paymentType, setPaymentType] = useState<'pix' | 'credit_card'>('pix');
  const [copiedPix, setCopiedPix] = useState(false);
  const [installments, setInstallments] = useState('1');
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');

  // Confirmation Protocol
  const [protocolCode, setProtocolCode] = useState('');

  useEffect(() => {
    if (initialSlot) {
      setSelectedSlot(initialSlot);
    }
    if (initialModality) {
      setModality(initialModality);
    } else {
      if (doctor.hasTelemedicine !== false) setModality('video');
      else if (doctor.hasInPerson) setModality('office');
      else if (doctor.hasHomeCare) setModality('home');
    }
  }, [initialSlot, initialModality, doctor]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      const randomCode = `MDK-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 90000)}`;
      setProtocolCode(randomCode);
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
        paymentPollingRef.current = null;
      }
    };
  }, [isOpen]);

  const priceByModality = {
    video: doctor.price || 120,
    office: doctor.inPersonPrice || 180,
    home: doctor.homeCarePrice || 250,
  };

  const currentPrice = priceByModality[modality];

  const { data: installmentData } = useQuery({
    queryKey: ['pagbank', 'installments', currentPrice],
    queryFn: () => paymentsApi.getPagBankInstallmentOptions(currentPrice),
    enabled: isOpen && currentPrice > 0,
  });

  const handleModalClose = () => {
    setCurrentStep(1);
    onClose();
  };

  if (!isOpen) return null;

  // Mask Helpers
  const formatCpf = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  const formatPhone = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const handleCopyPix = () => {
    const code = pixData?.copyPaste || pixData?.qrCode || '';
    if (code) {
      navigator.clipboard.writeText(code);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 3000);
    }
  };

  // ---------------------------------------------------------------------------
  // PagBank Payment Polling
  // ---------------------------------------------------------------------------
  const startPaymentPolling = (paymentId: string) => {
    if (paymentPollingRef.current) clearInterval(paymentPollingRef.current);
    paymentPollingRef.current = setInterval(async () => {
      try {
        const statusRes = await paymentsApi.getPaymentStatus(paymentId);
        if (statusRes.status === 'APPROVED' || statusRes.status === 'PAID') {
          clearInterval(paymentPollingRef.current!);
          paymentPollingRef.current = null;
          setPaymentConfirmed(true);
          toast({
            title: 'Pagamento confirmado!',
            description: 'Seu pagamento foi aprovado pela PagBank.',
            variant: 'success',
          });
          setTimeout(() => setCurrentStep(4), 1200);
        } else if (statusRes.status === 'FAILED' || statusRes.status === 'CANCELLED') {
          clearInterval(paymentPollingRef.current!);
          paymentPollingRef.current = null;
          setPaymentError('Pagamento recusado ou cancelado. Tente novamente.');
          setPixData(null);
        }
      } catch {
        // silently retry on next tick
      }
    }, 3000);
  };

  const handleConfirmBooking = async () => {
    setIsSubmitting(true);
    setPaymentProcessing(true);
    setPaymentError(null);

    const backendModality =
      modality === 'office'
        ? 'PRESENCIAL'
        : modality === 'home'
        ? 'DOMICILIAR'
        : 'TELEMEDICINA';

    let scheduledAtIso: string;
    if (selectedSlot.time === 'Agora') {
      // Para slots imediatos, agendar 3h à frente para respeitar MIN_BOOKING_NOTICE de 120 min
      scheduledAtIso = new Date(Date.now() + 3 * 60 * 60000).toISOString();
    } else {
      let dateIso = selectedSlot.fullDateIso;
      if (!dateIso) {
        const parts = selectedSlot.dateStr.split('/');
        const currentYear = new Date().getFullYear();
        if (parts.length === 2 && parts[0] && parts[1]) {
          dateIso = `${currentYear}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        } else if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
          dateIso = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        } else {
          dateIso = new Date().toISOString().split('T')[0];
        }
      }
      // Construir a data/hora no timezone local (não UTC) para evitar deslocamento
      const [hours, minutes] = selectedSlot.time.split(':').map(Number);
      const localDate = new Date(`${dateIso}T12:00:00`); // safe parse
      localDate.setHours(hours || 0, minutes || 0, 0, 0);

      // Se a data cai no passado ou dentro de 2h, empurrar para +3h à frente
      const minNoticeMs = 130 * 60 * 1000; // 130 min (margem sobre os 120 min do backend)
      if (localDate.getTime() - Date.now() < minNoticeMs) {
        localDate.setTime(Date.now() + 3 * 60 * 60000);
      }

      scheduledAtIso = localDate.toISOString();
    }

    try {
      // 1. Criar agendamento no backend
      let appointmentId: string | null = null;

      if (user?.id) {
        const created = await schedulingApi.createAppointment({
          doctorId: doctor.id,
          serviceType: backendModality as any,
          scheduledAt: scheduledAtIso,
          reason: patientComplaint.trim() || undefined,
        });
        appointmentId = created.id;
        setRealAppointmentId(created.id);
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
        queryClient.invalidateQueries({ queryKey: ['patient', 'appointments'] });
        queryClient.invalidateQueries({ queryKey: ['doctor', doctor.id, 'availability'] });
      }

      if (!appointmentId) {
        setPaymentError('Erro ao criar agendamento. Faça login e tente novamente.');
        setIsSubmitting(false);
        setPaymentProcessing(false);
        return;
      }

      // 2. Processar pagamento via PagBank
      const safePrice = currentPrice >= 1000 ? currentPrice / 100 : currentPrice;

      if (paymentType === 'pix') {
        // PIX via PagBank Order API
        const payRes = await paymentsApi.createPixPayment({
          appointmentId,
          patientId: user?.id || 'me',
          doctorId: doctor.id,
          amount: safePrice,
          description: `Consulta ${doctor.specialty} - ${doctor.name}`,
          patientName: patientName || user?.name || 'Paciente',
          patientEmail: patientEmail || user?.email || '',
          patientCpf: patientCpf.replace(/\D/g, '') || undefined,
          captchaToken,
          returnUrl: `${window.location.origin}/dashboard`,
        });

        setPixData({
          paymentId: payRes.paymentId,
          qrCode: payRes.qrCode,
          qrCodeUrl: payRes.qrCodeUrl,
          copyPaste: payRes.qrCode || payRes.checkoutUrl,
          checkoutUrl: payRes.checkoutUrl,
        });

        startPaymentPolling(payRes.paymentId);

        // Se PagBank retornou checkout hosted, redireciona
        if (payRes.checkoutUrl && !payRes.qrCode && !payRes.qrCodeUrl) {
          setTimeout(() => { window.location.href = payRes.checkoutUrl!; }, 800);
        }
      } else {
        // Cartão de Crédito via PagBank
        let encryptedCard: string | undefined;

        const config = await paymentsApi.getPagBankPublicConfig().catch(() => null);
        if (config?.publicKey && (window as any).PagSeguro?.encryptCard) {
          const [cleanMonth, cleanYear] = cardExpiry.split('/').map(s => s.trim());
          encryptedCard = (window as any).PagSeguro.encryptCard({
            publicKey: config.publicKey,
            holder: cardHolder || patientName || 'Paciente',
            number: cardNumber.replace(/\D/g, ''),
            expMonth: (cleanMonth || '12').padStart(2, '0'),
            expYear: cleanYear?.length === 2 ? `20${cleanYear}` : cleanYear || '2030',
            securityCode: cardCvv.trim(),
          })?.encryptedCard;
        }

        const cardRes = await paymentsApi.createCardPayment({
          appointmentId,
          patientId: user?.id || 'me',
          doctorId: doctor.id,
          amount: safePrice,
          description: `Consulta ${doctor.specialty} - ${doctor.name}`,
          patientName: cardHolder || patientName || 'Paciente',
          patientEmail: patientEmail || user?.email || '',
          patientCpf: patientCpf.replace(/\D/g, '') || undefined,
          installments: Number(installments) || 1,
          encryptedCard,
          securityCode: cardCvv,
          captchaToken,
        });

        if (cardRes?.checkoutUrl) {
          window.location.href = cardRes.checkoutUrl;
        } else {
          // Aprovação instantânea
          if (cardRes?.paymentId) {
            await paymentsApi.confirmPayment(cardRes.paymentId).catch(() => {});
          }
          setPaymentConfirmed(true);
          toast({
            title: 'Pagamento aprovado!',
            description: 'Seu cartão foi cobrado com sucesso.',
            variant: 'success',
          });
          setTimeout(() => setCurrentStep(4), 1200);
        }
      }
    } catch (err: any) {
      console.error('[DoctoraliaBookingModal] Erro no pagamento:', err);
      setPaymentError(err?.message || 'Erro ao processar pagamento. Tente novamente.');
    } finally {
      setIsSubmitting(false);
      setPaymentProcessing(false);
    }
  };

  const handleInModalLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setAuthError(null);
    if (!patientEmail.trim() || !authPassword) {
      setAuthError('Informe seu e-mail e senha.');
      return;
    }
    setIsAuthenticating(true);
    try {
      const res = await authApi.login(patientEmail.trim(), authPassword, captchaToken);
      if (res.accessToken) {
        httpClient.saveAccessToken(res.accessToken);
        await executeLogin(patientEmail.trim(), authPassword, res);
        setCurrentStep(3); // Avança direto para pagamento
      }
    } catch (err: any) {
      setAuthError(err?.message || 'E-mail ou senha incorretos.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleInModalRegister = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setAuthError(null);
    if (!patientName.trim() || !patientEmail.trim() || !authPassword) {
      setAuthError('Preencha seu nome, e-mail e crie uma senha.');
      return;
    }
    if (authPassword.length < 6) {
      setAuthError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    setIsAuthenticating(true);
    try {
      await authApi.register({
        name: patientName.trim(),
        email: patientEmail.trim(),
        password: authPassword,
        phone: patientPhone.trim() ? formatPhone(patientPhone) : undefined,
        cpf: patientCpf.trim() ? formatCpf(patientCpf) : undefined,
        lgpdConsent: true,
        termsConsent: true,
        captchaToken,
      });

      // Login automático após registrar
      const loginRes = await authApi.login(patientEmail.trim(), authPassword);
      if (loginRes.accessToken) {
        httpClient.saveAccessToken(loginRes.accessToken);
        await executeLogin(patientEmail.trim(), authPassword, loginRes);
        setCurrentStep(3); // Avança direto para pagamento
      }
    } catch (err: any) {
      setAuthError(err?.message || 'Erro ao realizar cadastro.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const canAdvanceToPayment =
    Boolean(user?.id) ||
    (patientName.trim().length >= 3 &&
      patientPhone.replace(/\D/g, '').length >= 10 &&
      patientEmail.includes('@'));

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center p-3 sm:p-4 overflow-y-auto overscroll-contain">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
        onClick={handleModalClose}
        onWheel={(e) => e.stopPropagation()}
      />

      {/* Modal Dialog Card */}
      <div className="relative w-full max-w-2xl bg-card text-card-foreground rounded-3xl border border-border/80 dark:border-white/10 shadow-2xl p-5 sm:p-7 z-10 overflow-hidden my-auto max-h-[92vh] flex flex-col isolate" onWheel={(e) => e.stopPropagation()}>
        {/* Top Header */}
        <div className="flex items-start justify-between pb-4 border-b border-border/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl overflow-hidden border border-border shrink-0 bg-primary/10 flex items-center justify-center font-extrabold text-primary">
              {doctor.photoUrl ? (
                <img
                  src={filesApi.getFileUrl(doctor.photoUrl)}
                  alt={doctor.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                doctor.name.slice(0, 2).toUpperCase()
              )}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-base sm:text-lg text-foreground">{doctor.name}</h3>
                <span title="Verificado no CFM">
                  <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {doctor.specialty} {doctor.crm && `· CRM ${doctor.crm}`}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleModalClose}
            className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper Progress Bar */}
        <div className="py-4 border-b border-border/40 shrink-0">
          <div className="flex items-center justify-between text-xs font-bold text-muted-foreground px-1 mb-2">
            <span className={cn(currentStep >= 1 ? 'text-primary font-extrabold' : '')}>1. Modalidade</span>
            <span className={cn(currentStep >= 2 ? 'text-primary font-extrabold' : '')}>2. Identificação</span>
            <span className={cn(currentStep >= 3 ? 'text-primary font-extrabold' : '')}>3. Pagamento</span>
            <span className={cn(currentStep >= 4 ? 'text-emerald-600 font-extrabold' : '')}>4. Confirmação</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-primary transition-all duration-300 rounded-full"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto py-5 space-y-5 pr-1">
          {/* STEP 1: Modalidade & Data */}
          {currentStep === 1 && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2.5">
                  Escolha o Formato de Atendimento
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {/* Telemedicina */}
                  <button
                    type="button"
                    disabled={doctor.hasTelemedicine === false}
                    onClick={() => setModality('video')}
                    className={cn(
                      'p-3.5 rounded-2xl border-2 text-left transition-all flex flex-col justify-between gap-3',
                      doctor.hasTelemedicine === false
                        ? 'opacity-40 border-border/50 bg-muted/20 cursor-not-allowed'
                        : modality === 'video'
                        ? 'border-primary bg-primary/5 text-primary shadow-sm cursor-pointer'
                        : 'border-border/80 hover:border-border text-foreground hover:bg-muted/40 cursor-pointer'
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="p-2 rounded-xl bg-cyan-500/10 text-cyan-600">
                        <Video className="w-5 h-5" />
                      </span>
                      {modality === 'video' && doctor.hasTelemedicine !== false && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-sm">Telemedicina</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Por vídeo no celular ou PC</p>
                      {doctor.hasTelemedicine === false ? (
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase mt-2 block">
                          Não habilitado
                        </span>
                      ) : (
                        <p className="text-sm font-extrabold text-primary mt-2">R$ {priceByModality.video}</p>
                      )}
                    </div>
                  </button>

                  {/* Consultório */}
                  <button
                    type="button"
                    disabled={doctor.hasInPerson === false}
                    onClick={() => setModality('office')}
                    className={cn(
                      'p-3.5 rounded-2xl border-2 text-left transition-all flex flex-col justify-between gap-3',
                      doctor.hasInPerson === false
                        ? 'opacity-40 border-border/50 bg-muted/20 cursor-not-allowed'
                        : modality === 'office'
                        ? 'border-primary bg-primary/5 text-primary shadow-sm cursor-pointer'
                        : 'border-border/80 hover:border-border text-foreground hover:bg-muted/40 cursor-pointer'
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="p-2 rounded-xl bg-indigo-500/10 text-indigo-600">
                        <Building2 className="w-5 h-5" />
                      </span>
                      {modality === 'office' && doctor.hasInPerson !== false && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-sm">Consultório</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Atendimento presencial</p>
                      {doctor.hasInPerson === false ? (
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase mt-2 block">
                          Não habilitado
                        </span>
                      ) : (
                        <p className="text-sm font-extrabold text-primary mt-2">R$ {priceByModality.office}</p>
                      )}
                    </div>
                  </button>

                  {/* Médico em Casa */}
                  <button
                    type="button"
                    disabled={doctor.hasHomeCare === false}
                    onClick={() => setModality('home')}
                    className={cn(
                      'p-3.5 rounded-2xl border-2 text-left transition-all flex flex-col justify-between gap-3',
                      doctor.hasHomeCare === false
                        ? 'opacity-40 border-border/50 bg-muted/20 cursor-not-allowed'
                        : modality === 'home'
                        ? 'border-primary bg-primary/5 text-primary shadow-sm cursor-pointer'
                        : 'border-border/80 hover:border-border text-foreground hover:bg-muted/40 cursor-pointer'
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600">
                        <Home className="w-5 h-5" />
                      </span>
                      {modality === 'home' && doctor.hasHomeCare !== false && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-sm">Médico em Casa</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Visita domiciliar</p>
                      {doctor.hasHomeCare === false ? (
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase mt-2 block">
                          Não habilitado
                        </span>
                      ) : (
                        <p className="text-sm font-extrabold text-primary mt-2">R$ {priceByModality.home}</p>
                      )}
                    </div>
                  </button>
                </div>
              </div>

              {/* Horário Selecionado */}
              <div className="p-4 rounded-2xl bg-muted/40 border border-border/80 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                      Horário Pré-Agendado
                    </span>
                    <span className="text-sm sm:text-base font-extrabold text-foreground">
                      {selectedSlot.dayLabel} ({selectedSlot.dateStr}) às {selectedSlot.time}
                    </span>
                  </div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                  Horário Reservado
                </span>
              </div>
            </div>
          )}

          {/* STEP 2: Identificação do Paciente */}
          {currentStep === 2 && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* Se o usuário já estiver logado */}
              {user?.id ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-800 dark:text-emerald-300 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-600 flex items-center justify-center font-black">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">
                          Identificado como Paciente
                        </span>
                        <span className="text-sm font-extrabold text-foreground">
                          {user.name || user.email}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                      Conta Conectada
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="p-3 rounded-xl border border-border bg-muted/20">
                      <span className="text-muted-foreground block">E-mail para Prescrições:</span>
                      <strong className="text-foreground">{patientEmail || user.email}</strong>
                    </div>
                    <div className="p-3 rounded-xl border border-border bg-muted/20">
                      <span className="text-muted-foreground block">WhatsApp para Notificações:</span>
                      <strong className="text-foreground">{patientPhone || 'Não informado'}</strong>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Motivo da Consulta / Sintomas (Opcional)
                    </label>
                    <textarea
                      value={patientComplaint}
                      onChange={(e) => setPatientComplaint(e.target.value)}
                      placeholder="Descreva brevemente o que você está sentindo..."
                      rows={2}
                      className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>
                </div>
              ) : (
                /* Paciente NÃO identificado: Exibe login rápido ou cadastro */
                <div className="space-y-4">
                  <div className="p-3.5 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-xs text-blue-900 dark:text-blue-300 flex items-start gap-2.5">
                    <Sparkles className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <span>
                      Para sua segurança e envio oficial das receitas digitais, identifique-se ou crie sua conta em 30 segundos.
                    </span>
                  </div>

                  {/* Abas Alternadoras: Entrar vs Criar Conta */}
                  <div className="grid grid-cols-2 p-1 rounded-xl bg-muted/60 border border-border text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('login');
                        setAuthError(null);
                      }}
                      className={cn(
                        'py-2 rounded-lg transition-all cursor-pointer',
                        authMode === 'login'
                          ? 'bg-card text-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      Já tenho conta (Entrar)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('register');
                        setAuthError(null);
                      }}
                      className={cn(
                        'py-2 rounded-lg transition-all cursor-pointer',
                        authMode === 'register'
                          ? 'bg-card text-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      Criar Conta Rápida
                    </button>
                  </div>

                  {/* Botão Oficial do Google */}
                  <div className="py-1">
                    <GoogleLoginButton
                      mode={authMode}
                      onError={(msg) => setAuthError(msg)}
                    />
                  </div>

                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-wider">
                      <span className="bg-card px-2.5 text-muted-foreground">ou use seu e-mail</span>
                    </div>
                  </div>

                  {/* Formulário Conforme Aba */}
                  {authMode === 'register' && (
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Nome Completo *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="text"
                          value={patientName}
                          onChange={(e) => setPatientName(e.target.value)}
                          placeholder="Seu nome completo"
                          className="w-full pl-10 pr-4 py-2 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                      </div>
                    </div>
                  )}

                  <div className={authMode === 'register' ? 'grid grid-cols-1 sm:grid-cols-2 gap-2.5' : 'space-y-2.5'}>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        E-mail *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="email"
                          value={patientEmail}
                          onChange={(e) => setPatientEmail(e.target.value)}
                          placeholder="seu@email.com"
                          className="w-full pl-10 pr-4 py-2 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        {authMode === 'register' ? 'Crie uma Senha *' : 'Sua Senha *'}
                      </label>
                      <input
                        type="password"
                        value={authPassword}
                        onChange={(e) => setAuthPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (authMode === 'login') handleInModalLogin();
                            else handleInModalRegister();
                          }
                        }}
                        placeholder="••••••••"
                        className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  {authMode === 'register' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                          CPF (opcional)
                        </label>
                        <input
                          type="text"
                          value={patientCpf}
                          onChange={(e) => setPatientCpf(formatCpf(e.target.value))}
                          placeholder="000.000.000-00"
                          maxLength={14}
                          className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                          WhatsApp / Celular
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="tel"
                            value={patientPhone}
                            onChange={(e) => setPatientPhone(formatPhone(e.target.value))}
                            placeholder="(11) 99999-9999"
                            maxLength={15}
                            className="w-full pl-10 pr-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {authError && (
                    <p className="text-xs text-rose-500 font-semibold px-1">{authError}</p>
                  )}

                  <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />

                  <Button
                    type="button"
                    disabled={isAuthenticating}
                    onClick={authMode === 'login' ? handleInModalLogin : handleInModalRegister}
                    className="w-full h-11 rounded-xl font-bold text-xs bg-primary text-white shadow-md shadow-primary/20"
                  >
                    {isAuthenticating
                      ? 'Processando...'
                      : authMode === 'login'
                      ? 'Entrar e Continuar para Pagamento'
                      : 'Criar Conta e Prosseguir'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Pagamento */}
          {currentStep === 3 && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2.5">
                  Forma de Pagamento
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentType('pix')}
                    className={cn(
                      'p-3 rounded-xl border-2 text-center transition-all cursor-pointer flex flex-col items-center gap-1.5',
                      paymentType === 'pix'
                        ? 'border-primary bg-primary/5 text-primary shadow-xs'
                        : 'border-border text-foreground hover:bg-muted/40'
                    )}
                  >
                    <QrCode className="w-5 h-5 text-emerald-500" />
                    <span className="text-xs font-bold">PIX Instantâneo</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentType('credit_card')}
                    className={cn(
                      'p-3 rounded-xl border-2 text-center transition-all cursor-pointer flex flex-col items-center gap-1.5',
                      paymentType === 'credit_card'
                        ? 'border-primary bg-primary/5 text-primary shadow-xs'
                        : 'border-border text-foreground hover:bg-muted/40'
                    )}
                  >
                    <CreditCard className="w-5 h-5 text-indigo-500" />
                    <span className="text-xs font-bold">Cartão em até 3x</span>
                  </button>
                </div>
              </div>

              {/* PIX View */}
              {paymentType === 'pix' && (
                <div className="p-4 rounded-2xl bg-muted/40 border border-border/80 text-center space-y-3">
                  {pixData ? (
                    <>
                      {/* QR Code real da PagBank */}
                      <div className="inline-flex p-3 rounded-2xl bg-white shadow-sm border border-border">
                        {pixData.qrCodeUrl ? (
                          <img
                            src={pixData.qrCodeUrl}
                            alt="QR Code PIX PagBank"
                            className="w-32 h-32 rounded-xl"
                          />
                        ) : (
                          <div className="w-32 h-32 bg-slate-900 rounded-xl flex items-center justify-center p-2">
                            <QrCode className="w-24 h-24 text-white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-bold text-foreground block">
                          Valor: R$ {currentPrice.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Aprovação instantânea via PagBank
                        </span>
                      </div>
                      {!paymentConfirmed && (
                        <div className="flex items-center justify-center gap-2 text-xs text-primary font-semibold animate-pulse">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Aguardando confirmação do pagamento...
                        </div>
                      )}
                      {paymentConfirmed && (
                        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-bold">
                          <CheckCircle2 className="w-4 h-4" />
                          Pagamento confirmado!
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCopyPix}
                        className="rounded-xl font-bold gap-2 w-full text-xs"
                      >
                        {copiedPix ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        <span>{copiedPix ? 'Código PIX Copiado!' : 'Copiar Código PIX Copia-e-Cola'}</span>
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Placeholder antes do pagamento ser iniciado */}
                      <div className="inline-flex p-3 rounded-2xl bg-white shadow-sm border border-border">
                        <div className="w-32 h-32 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center p-2">
                          <QrCode className="w-20 h-20 text-muted-foreground/40" />
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-bold text-foreground block">
                          Valor: R$ {currentPrice.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          O QR Code PIX será gerado ao confirmar o agendamento
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Credit Card View */}
              {paymentType === 'credit_card' && (
                <div className="space-y-3 p-4 rounded-2xl bg-muted/40 border border-border/80">
                  <div>
                    <label className="block text-[11px] font-bold text-muted-foreground uppercase mb-1">
                      Número do Cartão
                    </label>
                    <input
                      type="text"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                      placeholder="0000 0000 0000 0000"
                      className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-muted-foreground uppercase mb-1">
                      Nome Impresso no Cartão
                    </label>
                    <input
                      type="text"
                      value={cardHolder}
                      onChange={(e) => setCardHolder(e.target.value)}
                      placeholder="Como impresso no cartão"
                      className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-muted-foreground uppercase mb-1">
                        Validade
                      </label>
                      <input
                        type="text"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value.slice(0, 5))}
                        placeholder="MM/AA"
                        className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-muted-foreground uppercase mb-1">
                        CVV
                      </label>
                      <input
                        type="password"
                        value={cardCvv}
                        onChange={(e) => setCardCvv(e.target.value.slice(0, 4))}
                        placeholder="123"
                        maxLength={4}
                        className="w-full px-3.5 py-2 rounded-xl border border-input bg-background text-sm font-semibold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-muted-foreground uppercase mb-1">
                      Parcelamento
                    </label>
                    <select
                      value={installments}
                      onChange={(e) => setInstallments(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm font-semibold cursor-pointer"
                    >
                      {installmentData?.options && installmentData.options.length > 0 ? (
                        installmentData.options.map((opt) => (
                          <option key={opt.numero} value={opt.numero}>
                            {opt.descricao}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="1">1x de R$ {currentPrice.toFixed(2)} (Sem juros)</option>
                          <option value="2">2x de R$ {(currentPrice / 2).toFixed(2)} (Sem juros)</option>
                          <option value="3">3x de R$ {(currentPrice / 3).toFixed(2)} (Sem juros)</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              )}

              {/* Payment Error */}
              {paymentError && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-700 dark:text-rose-300 font-semibold">
                  {paymentError}
                </div>
              )}

              {/* Captcha para pagamento (token renovado para esta etapa) */}
              {!pixData && (
                <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />
              )}

              {/* PagBank Security Seal */}
              <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground pt-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Pagamento processado pela PagBank · Ambiente Criptografado</span>
              </div>
            </div>
          )}

          {/* STEP 4: Confirmação */}
          {currentStep === 4 && (
            <div className="space-y-5 text-center py-4 animate-in zoom-in-95 duration-200">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-inner">
                <CheckCircle2 className="w-9 h-9" />
              </div>

              <div>
                <h4 className="text-xl font-black text-foreground">Consulta Confirmada com Sucesso!</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Protocolo Oficial: <strong className="text-primary font-mono">{protocolCode}</strong>
                  {realAppointmentId && (
                    <span className="block mt-0.5 text-emerald-600 font-semibold text-[11px]">
                      Agendamento sincronizado: #{realAppointmentId.slice(0, 8)}
                    </span>
                  )}
                </p>
              </div>

              {/* Details Box */}
              <div className="p-4 rounded-2xl bg-muted/40 border border-border/80 text-left space-y-2 text-xs">
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Médico(a):</span>
                  <span className="font-bold text-foreground">{doctor.name}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Especialidade:</span>
                  <span className="font-bold text-foreground">{doctor.specialty}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Data e Horário:</span>
                  <span className="font-bold text-foreground">
                    {selectedSlot.dayLabel} ({selectedSlot.dateStr}) às {selectedSlot.time}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Modalidade:</span>
                  <span className="font-bold text-primary">
                    {modality === 'video' ? 'Telemedicina por Vídeo' : modality === 'office' ? 'Presencial em Consultório' : 'Médico em Casa'}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Paciente:</span>
                  <span className="font-bold text-foreground">{patientName || 'Você'}</span>
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-2 text-left">
                <Check className="w-4 h-4 shrink-0 text-emerald-600" />
                <span>Enviamos o link da sala virtual para seu WhatsApp ({patientPhone || 'cadastrado'}).</span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Bottom Actions */}
        <div className="pt-4 border-t border-border/60 flex items-center justify-between gap-3 shrink-0">
          {currentStep > 1 && currentStep < 4 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCurrentStep((prev) => (prev - 1) as any)}
              className="rounded-xl font-bold gap-1 text-xs"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Voltar</span>
            </Button>
          ) : (
            <div />
          )}

          {currentStep === 1 && (
            <Button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="rounded-xl font-bold gap-2 text-xs sm:text-sm bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white shadow-md shadow-primary/25 ml-auto px-6 h-11"
            >
              <span>Continuar para Identificação</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}

          {currentStep === 2 && user?.id && (
            <Button
              type="button"
              disabled={!canAdvanceToPayment}
              onClick={() => setCurrentStep(3)}
              className="rounded-xl font-bold gap-2 text-xs sm:text-sm bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white shadow-md shadow-primary/25 ml-auto px-6 h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Prosseguir para Pagamento</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}

          {currentStep === 3 && !paymentConfirmed && (
            <Button
              type="button"
              disabled={isSubmitting || paymentProcessing || (paymentType === 'pix' && !!pixData)}
              onClick={handleConfirmBooking}
              className="rounded-xl font-bold gap-2 text-xs sm:text-sm bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-md shadow-emerald-500/25 ml-auto px-6 h-11"
            >
              {isSubmitting || paymentProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processando Pagamento...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>{paymentType === 'pix' ? 'Gerar PIX e Confirmar' : 'Pagar e Confirmar'}</span>
                </>
              )}
            </Button>
          )}

          {currentStep === 4 && (
            <div className="flex items-center gap-2.5 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={handleModalClose}
                className="rounded-xl font-bold text-xs flex-1 h-11"
              >
                Fechar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  handleModalClose();
                  navigate(user ? '/dashboard' : '/login?redirect=/dashboard');
                }}
                className="rounded-xl font-bold gap-2 text-xs bg-primary text-white shadow-md shadow-primary/20 flex-1 h-11"
              >
                <span>{user ? 'Acessar Minhas Consultas' : 'Entrar / Conectar Conta'}</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
